import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FormControl,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { getRoles, getRole, normalizeRolesPayload } from '../../modules/users/redux/RoleCRUD';
import { getRolesByCompany, getRolesByLevel } from '../../modules/users/redux/PermissionCRUD';
import { RoleModel } from '../../modules/users/models/Role';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';

interface AutoSelectorProps {
  value: string[];
  onChange: (roles: string[]) => void;
  onBlur?: () => void;
  onOpen?: () => void;
  onInputChange?: (inputValue: string) => void;
  error?: boolean;
  disabled?: boolean;
  label?: string;
  labelWidth?: string | number;
  companyIds?: string[];
  groupByCompany?: boolean;
  fullWidth?: boolean;
  options?: Array<{ id: string; name?: string; display_name?: string; company_name?: string; company?: string; color?: string; avatar?: string | null; image1?: string | null; nickname?: string }>;
  multiple?: boolean;
  loading?: boolean;
  noOptionsText?: string;
  placeholder?: string;
  renderOption?: (props: any, option: any) => React.ReactNode;
}

export default function AutoSelector({ 
  value, 
  onChange, 
  onBlur, 
  onOpen,
  onInputChange,
  error = false, 
  disabled = false, 
  label = "Vai trò",
  labelWidth = 80,
  companyIds,
  groupByCompany = false,
  fullWidth = true,
  options,
  multiple = true,
  loading: externalLoading = false,
  noOptionsText = 'Không có dữ liệu',
  placeholder,
  renderOption,
}: AutoSelectorProps) {
  const [roles, setRoles] = useState<RoleModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  const hasLoadedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const lastCompanyIdsKeyRef = useRef<string>('');
  const isCustomOptionsMode = Array.isArray(options);

  const normalizedCustomOptions = useMemo(() => {
    if (!isCustomOptionsMode) return [];
    return (options || []).map((option) => ({
      ...option,
      id: String(option.id),
      display_name: option.display_name || option.name || String(option.id),
      name: option.name || option.display_name || String(option.id),
    }));
  }, [isCustomOptionsMode, options]);

  useEffect(() => {
    if (isCustomOptionsMode) {
      return;
    }

    const normalizedCompanyIds = Array.isArray(companyIds)
      ? companyIds
          .filter(Boolean)
          .map(String)
          .filter((id, index, arr) => arr.indexOf(id) === index)
      : [];
    const companyIdsKey = normalizedCompanyIds.join(',');

    // Explicit company scope mode (for Global users): load roles from all provided companies
    if (normalizedCompanyIds.length > 0) {
      if (lastCompanyIdsKeyRef.current === companyIdsKey && roles.length > 0) {
        return;
      }

      const fetchRolesByCompanies = async () => {
        setLoading(true);
        setErrorMessage(null);
        startGlobalLoading('Đang tải danh sách vai trò...', 'role-selector-load');
        try {
          const rolePromises = normalizedCompanyIds.map((companyId) => getRolesByCompany(companyId));
          const companyRoleResults = await Promise.all(rolePromises);
          const shouldIncludeGlobalRoles = normalizedCompanyIds.includes('global');
          const globalRoles = shouldIncludeGlobalRoles
            ? await getRolesByLevel('global').catch(() => [])
            : [];

          const mergedRoles = [...companyRoleResults.flat(), ...(Array.isArray(globalRoles) ? globalRoles : [])];
          const uniqueRolesMap = new Map<string, RoleModel>();

          mergedRoles.forEach((role: any) => {
            if (!role?.id) return;
            uniqueRolesMap.set(String(role.id), role as RoleModel);
          });

          setRoles(Array.from(uniqueRolesMap.values()));
          hasLoadedRef.current = true;
          lastCompanyIdsKeyRef.current = companyIdsKey;
        } catch (error) {
          setErrorMessage('Không thể tải danh sách vai trò');
        } finally {
          setLoading(false);
          stopGlobalLoading('role-selector-load');
          isFetchingRef.current = false;
        }
      };

      if (!isFetchingRef.current) {
        isFetchingRef.current = true;
        fetchRolesByCompanies();
      }

      return;
    }

    // Use global cache to prevent re-fetching
    const globalCache = (window as any).__rolesSelectorDataCache;
    
    // First check local state
    if (roles.length > 0) {
      hasLoadedRef.current = true;
      return;
    }
    
    // Then check global cache
    if (globalCache && globalCache.length > 0) {
      setRoles(globalCache);
      hasLoadedRef.current = true;
      return;
    }
    
    // Check if already fetching
    if (isFetchingRef.current) {
      return;
    }
    
    // Only fetch if not yet loaded
    if (!hasLoadedRef.current && !isFetchingRef.current) {
      isFetchingRef.current = true;
      hasLoadedRef.current = true;
      
      const fetchRoles = async () => {
        setLoading(true);
        setErrorMessage(null);
        startGlobalLoading('Đang tải danh sách vai trò...', 'role-selector-load');
        try {
          const response = await getRoles();
          const rolesData = normalizeRolesPayload(response.data);
          setRoles(rolesData);
          (window as any).__rolesSelectorDataCache = rolesData;
        } catch (error) {
          setErrorMessage('Không thể tải danh sách vai trò');
        } finally {
          setLoading(false);
          stopGlobalLoading('role-selector-load');
          isFetchingRef.current = false;
        }
      };

      fetchRoles();
    }
  }, [companyIds, isCustomOptionsMode, startGlobalLoading, stopGlobalLoading, roles.length]);

  const handleChange = (_event: any, newValue: any[] | any | null) => {
    const valueList = multiple
      ? (Array.isArray(newValue) ? newValue : [])
      : (newValue ? [newValue] : []);

    const selectedRoleIds = valueList.map((role) => {
      if (typeof role === 'object' && role !== null) {
        return role.id || role.role_id || role.code;
      }
      return role;
    });

    onChange(selectedRoleIds);

    // Clear the search input after selecting an option (multiple mode)
    if (multiple) {
      setInputValue('');
      // Do NOT call onInputChange('') here — that would trigger an
      // unwanted server fetch with an empty query.
    }
  };

  const getRoleLabel = (role: any): string => {
    if (!role) return '';
    return role.display_name || role.name || '';
  };

  const getRoleGroupLabel = (role: any): string => {
    if (role?.level === 'global') {
      return 'Toàn hệ thống';
    }

    const companyName = String(role?.company_name || role?.company || '').trim();
    if (companyName) {
      return companyName;
    }

    return 'Khác';
  };

  const roleOptions = useMemo(() => {
    if (isCustomOptionsMode) {
      return normalizedCustomOptions as any[];
    }

    if (!groupByCompany) {
      return roles;
    }

    return [...roles].sort((a: any, b: any) => {
      const groupA = getRoleGroupLabel(a);
      const groupB = getRoleGroupLabel(b);
      if (groupA !== groupB) {
        return groupA.localeCompare(groupB, 'vi');
      }

      const labelA = getRoleLabel(a);
      const labelB = getRoleLabel(b);
      return labelA.localeCompare(labelB, 'vi');
    });
  }, [groupByCompany, isCustomOptionsMode, normalizedCustomOptions, roles]);

  // Normalize comparison: convert both sides to strings for reliable matching
  // Must be memoized so the reference is stable — prevents MUI Autocomplete from
  // resetting inputValue when value reference changes on parent re-renders.
  const selectedRoles = useMemo(
    () =>
      Array.isArray(roleOptions) && Array.isArray(value)
        ? roleOptions.filter((role: any) => {
            const roleId = String(role.id || role.role_id || role.code);
            return value.some(vId => String(vId) === roleId);
          })
        : [],
    [roleOptions, value],
  );

  const selectedSingleColor = !multiple && selectedRoles[0]?.color
    ? String(selectedRoles[0].color)
    : undefined;

  useEffect(() => {
    if (multiple) {
      return;
    }

    const selectedLabel = selectedRoles[0] ? getRoleLabel(selectedRoles[0]) : '';
    setInputValue(selectedLabel);
  }, [multiple, selectedRoles]);

  // Fetch missing roles when value IDs are provided but not found in roles list
  useEffect(() => {
    if (isCustomOptionsMode) {
      return;
    }

    if (!Array.isArray(value) || value.length === 0 || roles.length === 0) {
      return;
    }

    const valueIds = value
      .map(vId => {
        if (typeof vId === 'object' && vId !== null) {
          return String((vId as any).id || (vId as any).role_id || '');
        }
        return String(vId);
      })
      .filter(id => id && id !== '[object Object]');
    
    const existingRoleIds = roles.map(r => String(r.id));
    const missingRoleIds = valueIds.filter(vId => !existingRoleIds.includes(vId));

    if (missingRoleIds.length > 0) {
      
      // Fetch each missing role
      Promise.all(
        missingRoleIds.map(roleId => 
          getRole(roleId)
            .then(response => {
              return response.data;
            })
            .catch(error => {
              return null;
            })
        )
      ).then(fetchedRoles => {
        const validRoles = fetchedRoles.filter(r => r !== null) as RoleModel[];
        if (validRoles.length > 0) {
          setRoles(prev => {
            const newRoles = [...prev];
            validRoles.forEach(fetchedRole => {
              if (!newRoles.some(r => String(r.id) === String(fetchedRole.id))) {
                newRoles.push(fetchedRole);
              }
            });
            // Update global cache
            (window as any).__rolesSelectorDataCache = newRoles;
            return newRoles;
          });
        }
      });
    }
  }, [isCustomOptionsMode, value, roles]);


  if (!isCustomOptionsMode && loading) {
    return (
      <LabeledField label={label} labelWidth={labelWidth} alignItems='end'>
        <FormControl fullWidth={fullWidth} size="small" disabled>
          <Box sx={sharedFormStyles.roleSelectorLoadingBox}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Đang tải vai trò...
            </Typography>
          </Box>
        </FormControl>
      </LabeledField>
    );
  }

  if (!isCustomOptionsMode && errorMessage) {
    return (
      <LabeledField label={label} error={true} helperText={errorMessage}>
        <FormControl fullWidth={fullWidth} size="small" error>
          <Box sx={sharedFormStyles.roleSelectorErrorBox}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        </FormControl>
      </LabeledField>
    );
  }

  return (
    <LabeledField label={label} error={error} labelWidth={labelWidth} alignItems='end'>
      <FormControl fullWidth={fullWidth} size="small" error={error}>
        <Autocomplete
          multiple={multiple}
          options={roleOptions}
          fullWidth
          getOptionLabel={(option) => getRoleLabel(option)}
          groupBy={!isCustomOptionsMode && groupByCompany ? (option) => getRoleGroupLabel(option) : undefined}
          value={multiple ? selectedRoles : (selectedRoles[0] || null)}
          inputValue={multiple ? inputValue : undefined}
          onChange={handleChange}
          onInputChange={(_, newInputValue, reason) => {
            // Block MUI's automatic 'reset' which fires when the value prop
            // reference changes (e.g. after a server fetch refreshes options).
            // Without this, the typed search text would be cleared mid-search.
            if (reason === 'reset') return;
            setInputValue(newInputValue);
            onInputChange?.(newInputValue);
          }}
          onBlur={onBlur}
          onOpen={onOpen}
          disabled={disabled}
          loading={isCustomOptionsMode ? externalLoading : loading}
          noOptionsText={noOptionsText}
          openOnFocus
          disableCloseOnSelect={multiple}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              error={error}
              placeholder={placeholder}
              sx={{
                ...sharedFormStyles.multiSelectInput,
                ...(selectedSingleColor
                  ? {
                      '& .MuiInputBase-input': {
                        color: selectedSingleColor,
                      },
                    }
                  : {}),
              }}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            multiple
              ? tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={getRoleLabel(option)}
                    size="small"
                    sx={{
                      ...sharedFormStyles.multiSelectChip,
                      ...(option?.color
                        ? {
                            '& .MuiChip-label': {
                              color: String(option.color),
                            },
                          }
                        : {}),
                    }}
                  />
                ))
              : []
          }
          renderOption={renderOption || ((props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box component="li" key={key} {...otherProps}>
                <Typography
                  variant="body2"
                  sx={{
                    ...sharedFormStyles.multiSelectOptionText,
                    ...(option?.color
                      ? {
                          color: String(option.color),
                        }
                      : {}),
                  }}
                >
                  {getRoleLabel(option)}
                </Typography>
              </Box>
            );
          })}
          renderGroup={(params) => (
            <Box
              component="li"
              key={params.key}
              sx={sharedFormStyles.multiSelectGroupContainer}
            >
              <Typography
                component="div"
                variant="body2"
                className="MuiAutocomplete-groupLabel"
                color="text.secondary"
                sx={{ fontWeight: 400, textTransform: 'none' }}
              >
                {params.group}
              </Typography>
              <Box component="ul" className="MuiAutocomplete-groupUl" sx={sharedFormStyles.multiSelectGroupList}>
                {params.children}
              </Box>
            </Box>
          )}
          isOptionEqualToValue={(option: any, selected: any) => String(option?.id) === String(selected?.id)}
          filterOptions={
            isCustomOptionsMode
              ? (options) => options
              : (options, { inputValue }) => {
                  const keyword = inputValue.toLowerCase();
                  const filtered = options.filter((option: any) =>
                    String(option.display_name || '').toLowerCase().includes(keyword) ||
                    String(option.name || '').toLowerCase().includes(keyword) ||
                    String(option.code || '').toLowerCase().includes(keyword) ||
                    String(option.company_name || option.company || '').toLowerCase().includes(keyword)
                  );
                  return filtered;
                }
          }
        />
      </FormControl>
    </LabeledField>
  );
}
